// ./src/components/features/Policies/PolicyEditDrawer/PolicyEditDrawer.tsx
//
// Create/edit drawer for a Policy (account-level reusable waiver / consent /
// house-rule). Self-loading in edit mode via recordId; context-or-props
// resolution; no success toast (the page highlight is the confirmation).
// Fields: name, acceptance label, full policy text (+ format), age range,
// how guests accept it (a tick box, or a link to sign — #164) and what the signing page collects
// (initials / signature, or neither = an Accept button), internal note, status.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, NumberInput, Switch, Group, Divider, Text, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { Policy, PolicyInput, POLICY_ACCEPTANCE_METHOD, PolicyAcceptanceMethod, POLICY_AGE_MIN, POLICY_AGE_MAX } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalPoliciesContext } from "@/contexts/PoliciesContext";
import { AppDrawer, SegmentedField, StatusInput } from "@/components/generic";

import { PolicyTextEditor } from "../PolicyTextEditor/PolicyTextEditor";

export interface PolicyEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode (e.g. from a "Create X" action). */
  initialName?: string | null;
  onCreated?: (policy: Policy) => void;
  onUpdated?: (policy: Policy) => void;
}

interface FormData {
  name: string;
  label: string;
  policyText: string;
  contentFormat: string;
  minAge: number | null;
  maxAge: number | null;
  requireInitials: boolean;
  requireSignature: boolean;
  acceptanceMethod: PolicyAcceptanceMethod;
  description: string;
  status: number;
}

const KNOWN_FORM_FIELDS = [
  "name",
  "label",
  "policyText",
  "contentFormat",
  "minAge",
  "maxAge",
  "requireInitials",
  "requireSignature",
  "acceptanceMethod",
  "description",
  "status",
];

const getInitialFormData = (): FormData => ({
  name: "",
  label: "",
  policyText: "",
  contentFormat: "richtext",
  minAge: null,
  maxAge: null,
  requireInitials: false,
  requireSignature: false,
  acceptanceMethod: POLICY_ACCEPTANCE_METHOD.SIGN,
  description: "",
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: Policy): FormData => ({
  name: record.name || "",
  label: record.label || "",
  policyText: record.policyText || "",
  contentFormat: record.contentFormat || "markdown",
  minAge: record.minAge ?? null,
  maxAge: record.maxAge ?? null,
  requireInitials: record.requireInitials ?? false,
  requireSignature: record.requireSignature ?? false,
  acceptanceMethod: record.acceptanceMethod === POLICY_ACCEPTANCE_METHOD.CHECKBOX ? POLICY_ACCEPTANCE_METHOD.CHECKBOX : POLICY_ACCEPTANCE_METHOD.SIGN,
  description: record.description || "",
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const toAge = (value: number | string): number | null => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const n = Number(value);

  return Number.isFinite(n) ? n : null;
};

const PolicyEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const PolicyEditDrawer: React.FC<PolicyEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["policies", "common"]);

  const ctx = useOptionalPoliciesContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Policy | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Policy);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  // The text editor is in full screen. ⚠ One Esc there would close full screen AND this drawer (Mantine's listeners sit on
  // the window and none stops it), losing what was typed — so the drawer's own Esc, click-outside and shortcuts are off
  // meanwhile (the Schedule drawer's fix, 2026-09-30).
  const [textFullscreen, setTextFullscreen] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record)
      : { ...getInitialFormData(), name: initialName?.trim() || "" };
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData: PolicyInput = {
      name: formData.name.trim() || null,
      label: formData.label.trim() || null,
      policyText: formData.policyText.trim() || null,
      contentFormat: formData.contentFormat,
      minAge: formData.minAge,
      maxAge: formData.maxAge,
      requireInitials: formData.requireInitials,
      requireSignature: formData.requireSignature,
      acceptanceMethod: formData.acceptanceMethod,
      description: formData.description.trim() || null,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Policy[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened && !textFullscreen,
  });

  const hasText = !!formData.policyText.trim();
  const isCheckbox = formData.acceptanceMethod === POLICY_ACCEPTANCE_METHOD.CHECKBOX;

  const drawerTitle = isEditMode
    ? t("policies:form.editTitle", { defaultValue: "Edit policy" })
    : t("policies:form.addTitle", { defaultValue: "Add policy" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting && !textFullscreen}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <PolicyEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("policies:form.fields.name.label", { defaultValue: "Name" })}
            description={t("policies:form.fields.name.description", {
              defaultValue: "Shown to the guest as the title of the policy.",
            })}
            placeholder={t("policies:form.fields.name.placeholder", { defaultValue: "e.g. Liability Waiver" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <TextInput
            label={t("policies:form.fields.label.label", { defaultValue: "Acceptance line" })}
            description={t("policies:form.fields.label.description", {
              defaultValue: "The checkbox text the guest ticks to accept.",
            })}
            placeholder={t("policies:form.fields.label.placeholder", {
              defaultValue: "e.g. I have read and accept this waiver",
            })}
            value={formData.label}
            onChange={(e) => setField("label", e.currentTarget.value)}
            error={fieldErrors.label}
            size="md"
          />

          <PolicyTextEditor
            label={t("policies:form.fields.policyText.label", { defaultValue: "Policy text" })}
            description={t("policies:form.fields.policyText.description", {
              defaultValue: "Leave empty to show a simple acceptance checkbox — no full document dialog.",
            })}
            placeholder={t("policies:form.fields.policyText.placeholder", {
              defaultValue: "The full text of the waiver / consent / house rule…",
            })}
            format={formData.contentFormat}
            onFormatChange={(f) => setField("contentFormat", f)}
            value={formData.policyText}
            onChange={(v) => setField("policyText", v)}
            onFullscreenChange={setTextFullscreen}
            error={fieldErrors.policyText || fieldErrors.contentFormat}
          />

          <Divider
            label={t("policies:form.sections.applicability", { defaultValue: "Who it applies to" })}
            labelPosition="left"
          />

          <Group grow align="flex-start">
            <NumberInput
              label={t("policies:form.fields.minAge.label", { defaultValue: "Min age" })}
              placeholder={t("policies:form.fields.minAge.placeholder", { defaultValue: "No lower bound" })}
              value={formData.minAge ?? ""}
              onChange={(v) => setField("minAge", toAge(v))}
              error={fieldErrors.minAge}
              min={POLICY_AGE_MIN}
              max={POLICY_AGE_MAX}
              allowNegative={false}
              allowDecimal={false}
              size="md"
            />
            <NumberInput
              label={t("policies:form.fields.maxAge.label", { defaultValue: "Max age" })}
              placeholder={t("policies:form.fields.maxAge.placeholder", { defaultValue: "No upper bound" })}
              value={formData.maxAge ?? ""}
              onChange={(v) => setField("maxAge", toAge(v))}
              error={fieldErrors.maxAge}
              min={POLICY_AGE_MIN}
              max={POLICY_AGE_MAX}
              allowNegative={false}
              allowDecimal={false}
              size="md"
            />
          </Group>
          <Text size="xs" c="dimmed" mt={-8}>
            {t("policies:form.fields.age.hint", {
              defaultValue:
                "Leave both empty to apply to all guests. Either bound needs each guest's date of birth, which comes from a date question on the event's form linked to the person's birthday.",
            })}
          </Text>

          <Divider
            label={t("policies:form.sections.signing", { defaultValue: "How guests accept it" })}
            labelPosition="left"
          />

          {/* #164 (owner, 2026-10-15/16): a tick box on the form is enough for most terms — no email, and no date of
              birth unless an age range is set. The signed link stays for what needs one; the copy says which to pick.
              A policy without a text is always a tick box, so the choice appears only with one. */}
          {hasText ? (
            <>
              <SegmentedField
                name="acceptanceMethod"
                label={t("policies:form.fields.acceptanceMethod.label", { defaultValue: "How guests accept it" })}
                data={[
                  { value: POLICY_ACCEPTANCE_METHOD.CHECKBOX, label: t("policies:form.fields.acceptanceMethod.checkbox", { defaultValue: "Tick a box" }) },
                  { value: POLICY_ACCEPTANCE_METHOD.SIGN, label: t("policies:form.fields.acceptanceMethod.sign", { defaultValue: "Sign by a link" }) },
                ]}
                value={formData.acceptanceMethod}
                onChange={(_name, v) => setField("acceptanceMethod", v === POLICY_ACCEPTANCE_METHOD.CHECKBOX ? POLICY_ACCEPTANCE_METHOD.CHECKBOX : POLICY_ACCEPTANCE_METHOD.SIGN)}
                error={fieldErrors.acceptanceMethod}
                fullWidth
                size="md"
              />
              <Text size="md" c="dimmed" mt={-8}>
                {isCheckbox
                  ? t("policies:form.fields.acceptanceMethod.checkboxHint", {
                      defaultValue:
                        "Whoever registers ticks “I agree” for each attendee when they RSVP or buy tickets — the text is one tap away. Nothing is emailed, and no date of birth is asked unless you set an age range above. Good for terms of entry, house rules and photo consent.",
                    })
                  : t("policies:form.fields.acceptanceMethod.signHint", {
                      defaultValue:
                        "Each adult is emailed a link to read and accept it on their own device; for anyone under the event's age of majority, the parent or guardian is emailed it instead. The form then needs each attendee's date of birth and an email. Use it for a release of liability, or anything a guardian must sign.",
                    })}
              </Text>
            </>
          ) : (
            <Text size="md" c="dimmed">
              {t("policies:form.fields.acceptanceMethod.noText", {
                defaultValue: "Without a policy text, guests accept it with a tick box when they RSVP or buy tickets — nothing is emailed.",
              })}
            </Text>
          )}

          {/* What the signing page collects — only a policy accepted by link has one. Neither switch = an Accept button. */}
          {hasText && !isCheckbox && (
            <>
              <Switch
                label={t("policies:form.fields.requireSignature.label", { defaultValue: "Require a signature" })}
                description={t("policies:form.fields.requireSignature.description", {
                  defaultValue: "They draw their signature on the page, with their legal name.",
                })}
                checked={formData.requireSignature}
                onChange={(e) => setField("requireSignature", e.currentTarget.checked)}
                error={fieldErrors.requireSignature}
                size="md"
              />

              <Switch
                label={t("policies:form.fields.requireInitials.label", { defaultValue: "Require initials" })}
                description={t("policies:form.fields.requireInitials.description", {
                  defaultValue: "They add their initials on the page, with their legal name.",
                })}
                checked={formData.requireInitials}
                onChange={(e) => setField("requireInitials", e.currentTarget.checked)}
                error={fieldErrors.requireInitials}
                size="md"
              />

              <Text size="md" c="dimmed" mt={-4}>
                {!formData.requireSignature && !formData.requireInitials
                  ? t("policies:form.fields.signing.acceptOnly", {
                      defaultValue: "Neither is on, so the page shows the text and an Accept button.",
                    })
                  : t("policies:form.fields.signing.withMark", {
                      defaultValue: "The page shows the text, asks for their legal name, and collects what is switched on.",
                    })}{" "}
                {/* Who receives the link is decided by AGE, and the threshold is a property of the event, not of this
                    policy — a policy is reusable across events in different places. So this names the rule and the
                    default rather than a number it cannot know. */}
                {t("policies:form.fields.signing.minorHint", {
                  defaultValue:
                    "The event's age of majority is 18 unless you change it on its Details & settings page — it is 19 in BC, 16 in Scotland, 21 in Mississippi.",
                })}
              </Text>
            </>
          )}

          <Divider />

          <Textarea
            label={t("policies:form.fields.description.label", { defaultValue: "Internal note" })}
            description={t("policies:form.fields.description.description", {
              defaultValue: "Only your team sees this — never shown to guests.",
            })}
            placeholder={t("policies:form.fields.description.placeholder", {
              defaultValue: "e.g. Reviewed by legal on 2026-01-01",
            })}
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
